import React, { useMemo } from "react";
import { useNotaire } from "../context/NotaireContext";
import { Link } from "react-router-dom";
import EmptyState from "./EmptyState";
import { CalendarDateRangeIcon, CalendarIcon } from "@heroicons/react/24/outline";
import TypeAffaireBadge from "./TypeAffaireBadge";

/* ===============================
   Helpers
================================ */

function safeLower(v) {
  return String(v ?? "").trim().toLowerCase();
}

/**
 * Date + heure intelligente
 * - gère "dd/MM" sans année
 * - bascule sur l'année suivante si déjà passé par rapport à date_creation
 */
function parseRdvStart(fields) {
  if (!fields?.date) return null;

  // Airtable: souvent date_creation ISO, sinon createdTime, sinon fallback maintenant
  const createdAtRaw = fields?.date_creation || fields?.createdTime || null;
  const createdAt = createdAtRaw ? new Date(createdAtRaw) : new Date();
  if (Number.isNaN(createdAt.getTime())) return null;

  // date = "dd/MM"
  const [ddStr, mmStr] = String(fields.date).split("/");
  const dd = parseInt(ddStr, 10);
  const mm = parseInt(mmStr, 10);
  if (!dd || !mm) return null;

  // heure_debut : "9", "09", "09:30"
  const hStr = String(fields.heure_debut ?? "0").trim();
  const [hhStr, minStr] = hStr.includes(":") ? hStr.split(":") : [hStr, "0"];
  const hh = parseInt(hhStr, 10);
  const min = parseInt(minStr, 10);
  const safeH = Number.isFinite(hh) ? hh : 0;
  const safeM = Number.isFinite(min) ? min : 0;

  // 1) année de création
  let year = createdAt.getFullYear();
  let rdv = new Date(year, mm - 1, dd, safeH, safeM, 0, 0);

  // 2) si rdv < createdAt => année suivante (déc -> jan)
  if (rdv.getTime() < createdAt.getTime()) {
    rdv = new Date(year + 1, mm - 1, dd, safeH, safeM, 0, 0);
  }

  return rdv;
}

/* ===============================
   Badge statut
================================ */
function StatutBadge({ statut }) {
  const s = safeLower(statut);

  if (s === "valide" || s === "validé") {
    return (
      <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">
        Validé
      </span>
    );
  }

  if (s === "attente validation") {
    return (
      <span className="inline-flex items-center rounded-md bg-orange-100 px-2 py-1 text-xs font-bold text-orange-800">
        À valider
      </span>
    );
  }

  if (s === "annule" || s === "annulé") {
    return (
      <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-bold text-red-800">
        Annulé
      </span>
    );
  }

  return null;
}

/* ===============================
   Composant principal
================================ */
const AllRdvDashboard = () => {
  const context = useNotaire();
  const demandes = Array.isArray(context?.demandeRdv) ? context.demandeRdv : [];

  const lastFive = useMemo(() => {
    const now = new Date();

    return demandes
      .filter((rec) => {
        const s = safeLower(rec?.fields?.statut);
        // ✅ on ignore les RDV annulés
        return s !== "annule" && s !== "annulé";
      })
      .filter((rec) => {
        const s = safeLower(rec?.fields?.statut);
        // ✅ on ignore les RDV a verifier
        return s !== "a verifier";
      })
      .map((rec) => ({
        ...rec,
        parsedStart: parseRdvStart(rec.fields),
      }))
      .filter((rec) => rec.parsedStart instanceof Date && !Number.isNaN(rec.parsedStart.getTime()))
      .filter((rec) => rec.parsedStart.getTime() >= now.getTime()) // ✅ uniquement à venir
      .sort((a, b) => a.parsedStart.getTime() - b.parsedStart.getTime())
      .slice(0, 5);
  }, [demandes]);

  if (!context) return null;

  return (
    <div>
      {lastFive.length === 0 ? (
        <div className="overflow-hidden rounded-lg bg-white shadow-sm mt-3 text-center">
          <EmptyState
            icon={CalendarIcon}
            title="Aucune demande de rendez-vous à venir"
            description="Les prochains rendez-vous apparaîtront ici"
          />
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {lastFive.map(({ id, fields }) => {
            const statutLower = safeLower(fields?.statut);
            const isValide = statutLower === "valide" || statutLower === "validé";

            return (
              <div
                key={id}
                className="flex flex-col rounded-lg bg-white p-4 transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <div className="rounded-md bg-indigo-50 p-2">
                      <CalendarDateRangeIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {fields.date}
                      </p>
                      <p className="text-xs text-gray-500">
                        {fields.heure_debut}h à {fields.heure_fin}h
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <TypeAffaireBadge type={fields.type_affaire} />
                    <StatutBadge statut={fields.statut} />
                  </div>
                </div>

                {/* Client */}
                <div className="mt-3 space-y-1">
                  <p className="text-md font-semibold text-gray-900 capitalize">
                    {fields.nom_from_user} {fields.prenom_from_user}
                  </p>
                  <p className="text-xs text-gray-500">
                    {fields.type_rdv === "presentiel" && "Rendez-vous à l'étude"}
                    {fields.type_rdv === "telephone" && "Rendez-vous par téléphone"}
                    {fields.type_rdv === "visio" && "Rendez-vous en visio"}
                    {!fields.type_rdv && "Rendez-vous (type non renseigné)"}
                    {fields.adresse ? (
                      <>
                        <br />
                        {fields.adresse}
                      </>
                    ) : null}
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-3 flex gap-2">
                  <Link
                    to={`/rdv/${id}`}
                    target="_blank"
                    className="btn-secondary rounded-full text-xs px-3 py-1.5 w-full text-center"
                  >
                    Modifier
                  </Link>

                  {!isValide && (
                    <Link
                      to={`/rdv/${id}`}
                      target="_blank"
                      className="btn-main rounded-full text-xs px-3 py-1.5 w-full text-center"
                    >
                      Valider
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AllRdvDashboard;